/**
 * Window Focus Detection Service
 * 
 * Detects when the browser window is not in focus and manages
 * background conversation preferences.
 * 
 * When the user is speaking but the window is not focused, the AI
 * should ask if they're talking to it or someone else.
 */

import { bus } from '../../core/events.js';
import { state, updateState } from '../../core/state.js';

let isWindowFocused = !document.hidden;
let lastFocusChangeTime = Date.now();
let lastMouseActivity = Date.now();
let inactiveTimer: ReturnType<typeof setTimeout> | null = null;
const INACTIVE_THRESHOLD = 30000; // 30 seconds of no mouse activity

/**
 * Initialize window focus detection
 */
export function initializeWindowFocusDetection(): void {
  // Initialize state if not present
  if (!state.windowFocus) {
    updateState('windowFocus', (draft) => {
      draft.isFocused = isWindowFocused;
      draft.allowBackgroundConversation = null;
      draft.lastFocusCheckDate = new Date().toISOString().split('T')[0]; // Today's date
      draft.lastAskedDate = '';
    });
  }

  // Listen for visibility changes
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  // Listen for window focus/blur events
  window.addEventListener('focus', handleWindowFocus);
  window.addEventListener('blur', handleWindowBlur);

  // Track mouse activity for inactive/active detection
  document.addEventListener('mousemove', handleMouseActivity);
  document.addEventListener('mousedown', handleMouseActivity);
  document.addEventListener('mouseup', handleMouseActivity);
  document.addEventListener('click', handleMouseActivity);

  // Start inactive timer
  startInactiveTimer();

  // Update initial state
  updateState('windowFocus', (draft) => {
    draft.isFocused = isWindowFocused;
  });

}

/**
 * Handle visibility change (tab switching)
 */
function handleVisibilityChange(): void {
  const wasFocused = isWindowFocused;
  isWindowFocused = !document.hidden;
  lastFocusChangeTime = Date.now();

  console.log(`[Window Focus E2E] VISIBILITY CHANGE: ${wasFocused} → ${isWindowFocused}, document.hidden=${document.hidden}`);

  updateState('windowFocus', (draft) => {
    draft.isFocused = isWindowFocused;
    draft.lastFocusCheckDate = new Date().toISOString().split('T')[0];
  });

  console.log(`[Window Focus E2E] → Emitting window.focus.changed event: isFocused=${isWindowFocused}`);
  bus.emit('window.focus.changed', {
    isFocused: isWindowFocused,
    timestamp: lastFocusChangeTime
  });
  console.log(`[Window Focus E2E] → Event emitted, metaball should respond`);
}

/**
 * Handle window focus
 */
function handleWindowFocus(): void {
  const wasFocused = isWindowFocused;
  isWindowFocused = true;
  lastFocusChangeTime = Date.now();

  console.log(`[Window Focus E2E] WINDOW FOCUS: ${wasFocused} → ${isWindowFocused}`);

  updateState('windowFocus', (draft) => {
    draft.isFocused = isWindowFocused;
    draft.lastFocusCheckDate = new Date().toISOString().split('T')[0];
  });

  console.log(`[Window Focus E2E] → Emitting window.focus.changed event: isFocused=${isWindowFocused}`);
  bus.emit('window.focus.changed', {
    isFocused: isWindowFocused,
    timestamp: lastFocusChangeTime
  });
  console.log(`[Window Focus E2E] → Event emitted, metaball should respond`);
}

/**
 * Handle window blur
 */
function handleWindowBlur(): void {
  const wasFocused = isWindowFocused;
  isWindowFocused = false;
  lastFocusChangeTime = Date.now();

  console.log(`[Window Focus E2E] WINDOW BLUR: ${wasFocused} → ${isWindowFocused}`);

  updateState('windowFocus', (draft) => {
    draft.isFocused = isWindowFocused;
    draft.lastFocusCheckDate = new Date().toISOString().split('T')[0];
  });

  console.log(`[Window Focus E2E] → Emitting window.focus.changed event: isFocused=${isWindowFocused}`);
  bus.emit('window.focus.changed', {
    isFocused: isWindowFocused,
    timestamp: lastFocusChangeTime
  });
  console.log(`[Window Focus E2E] → Event emitted, metaball should respond`);
}

/**
 * Check if window is currently focused
 * Uses multiple checks for reliability
 */
export function isWindowFocusedNow(): boolean {
  // Check document.hasFocus() as primary check (most reliable)
  // Also check our internal state and visibility
  const hasFocus = document.hasFocus();
  const isVisible = !document.hidden;
  const internalStateFocused = isWindowFocused;
  
  // Window is focused if:
  // 1. document.hasFocus() returns true (most reliable)
  // 2. OR (document is visible AND our internal state says focused)
  // This handles edge cases where hasFocus() might be false but user is actively interacting
  const focused = hasFocus || (isVisible && internalStateFocused);
  return focused;
}

/**
 * Check if we should ask about background conversation
 * (Only ask once per day)
 */
export function shouldAskAboutBackgroundConversation(): boolean {
  if (!state.windowFocus) {
    return false;
  }

  const today = new Date().toISOString().split('T')[0];
  const lastAsked = state.windowFocus.lastAskedDate;

  // If we've already asked today, don't ask again
  if (lastAsked === today) {
    return false;
  }

  // If user has already set a preference, don't ask again (unless it's a new day)
  // Actually, we should ask once per day regardless of preference
  // This allows the user to change their mind day-to-day
  return true;
}

/**
 * Check if user has allowed background conversations
 */
export function isBackgroundConversationAllowed(): boolean {
  if (!state.windowFocus) {
    return false;
  }
  const allowed = state.windowFocus.allowBackgroundConversation === true;
  return allowed;
}

/**
 * Set user preference for background conversations
 */
export function setBackgroundConversationPreference(allowed: boolean): void {
  const today = new Date().toISOString().split('T')[0];
  
  updateState('windowFocus', (draft) => {
    draft.allowBackgroundConversation = allowed;
    draft.lastAskedDate = today;
  });

  bus.emit('window.background.conversation.preference.changed', {
    allowed,
    timestamp: Date.now()
  });
}

/**
 * Mark that we've asked about background conversation today
 */
export function markAskedAboutBackgroundConversationToday(): void {
  const today = new Date().toISOString().split('T')[0];
  
  updateState('windowFocus', (draft) => {
    draft.lastAskedDate = today;
  });
}

/**
 * Handle mouse activity (for inactive/active detection)
 */
function handleMouseActivity(): void {
  const timeSinceLastActivity = Date.now() - lastMouseActivity;
  const wasInactive = timeSinceLastActivity > INACTIVE_THRESHOLD;
  lastMouseActivity = Date.now();

  // If we were inactive and now have activity, emit active event
  if (wasInactive) {
    console.log(`[Window Focus E2E] APP ACTIVE: Mouse activity detected after ${timeSinceLastActivity}ms of inactivity`);
    console.log(`[Window Focus E2E] → Emitting app.active event`);
    bus.emit('app.active', {
      timestamp: Date.now(),
      inactiveDuration: timeSinceLastActivity
    });
    console.log(`[Window Focus E2E] → Event emitted, metaball should respond`);
  }

  // Reset inactive timer
  resetInactiveTimer();
}

/**
 * Start inactive timer
 */
function startInactiveTimer(): void {
  resetInactiveTimer();
}

/**
 * Reset inactive timer
 */
function resetInactiveTimer(): void {
  if (inactiveTimer) {
    clearTimeout(inactiveTimer);
  }

  inactiveTimer = setTimeout(() => {
    // Check if we're still inactive
    const timeSinceLastActivity = Date.now() - lastMouseActivity;
    if (timeSinceLastActivity >= INACTIVE_THRESHOLD) {
      console.log(`[Window Focus E2E] APP INACTIVE: No mouse activity for ${timeSinceLastActivity}ms (threshold: ${INACTIVE_THRESHOLD}ms)`);
      console.log(`[Window Focus E2E] → Emitting app.inactive event`);
      bus.emit('app.inactive', {
        timestamp: Date.now(),
        inactiveDuration: timeSinceLastActivity
      });
      console.log(`[Window Focus E2E] → Event emitted, metaball should respond`);
    }
  }, INACTIVE_THRESHOLD);
}

